
  import { mapActions, mapState } from 'vuex'
  import debounce from 'lodash.debounce'

  import AppIconTextBlock from '@loavies/component-library/dist/app-icon-text-block'
  import IconLabelBorderless from '@loavies/component-library/dist/icons/icon-label-borderless'

  export default {
    name: 'ListingsPage',
    components: {
      AppIconTextBlock,
      IconLabelBorderless,
    },
    async asyncData(context) {
      const navigation = context.$staticDataService.globalNavigation?.navigation
      const categorySlug = context.route.params.category?.trim().toLowerCase()
      const subcategorySlug = context.route.params.subcategory?.trim().toLowerCase()
      let currentCategory = navigation.find(listingCategory => listingCategory.slug === categorySlug)

      if (
        !currentCategory
        || (subcategorySlug && !currentCategory.subcategories.some(listingSubcategory => listingSubcategory.slug === subcategorySlug))
      ) {
        return context.error({ statusCode: 404, message: 'Page not found' })
      }

      // Set current category in store
      if (subcategorySlug) {
        currentCategory = currentCategory.subcategories.find(listingSubcategory => listingSubcategory.slug === subcategorySlug)
      }

      await context.store.dispatch('listings/setActiveCategory', currentCategory || null)

      // Get Dato content
      const { page } = await context.$datoApiService.getPageDataWithCustomQueryPath(
        subcategorySlug ? 'listings/_subcategory-slug' : 'listings/_category-slug',
        currentCategory.slug
      )

      // Set listing page content from Dato in store.
      // This is shown on the CategoryLayoutPage (pages/listings.vue)
      await context.store.dispatch('listings/setListingsPageContent', page?.content ?? [])

      return { currentCategory, page }
    },
    data() {
      return {
        page: null, // Is set in asyncData
        currentCategory: null, // Is set in asyncData
        loading: false,
        handlePopstateDebounced: debounce(() => this.handlePopstate(), 300),
      }
    },
    head() {
      return {
        title: this.$pageHelperService.getFormattedPageTitle(this.currentCategory.name),
      }
    },
    computed: {
      ...mapState({
        showLargeProductCards: state => state.showLargeProductCards,
        previousCategoryRoutePath: state => state.routes.previousCategoryRoutePath,
        activeListingsTotals: state => state.listings.totals,
        listings: state => state.listings.listings,
        activeFilters: state => state.listings.activeFilters,
        activePriceFilters: state => state.listings.activePriceFilters,
        activeSortOption: state => state.listings.activeSortOption,
      }),
      emptyStateTitle() {
        return this.activeFilters.length ? this.$t('no_listings_after_filter_title') : this.$t('no_listings_in_category_title')
      },
      emptyStateText() {
        return this.activeFilters.length ? this.$t('no_listings_after_filter_text') : this.$t('no_listings_in_category_text')
      },
      hasMoreListings() {
        if (!this.listings || !this.activeListingsTotals) {
          return false
        }

        return this.listings.length < this.activeListingsTotals.totalItems
      },
    },
    async mounted() {
      // Backwards/forwards listener to sync filters with query params
      window.addEventListener('popstate', this.handlePopstateDebounced)

      // Check if filters are same in store and query
      const currentActiveFiltersEncodedQuery = this.$filterQueryHelperService.formatQueryFromFilters(this.activeFilters)?.filters
      const currentRouteFiltersEncodedQuery = this.$route.query.filters
      const routeFiltersAreEqualToActiveFilters = currentRouteFiltersEncodedQuery === currentActiveFiltersEncodedQuery

      // Check if price filters are same in store and query
      const currentActivePriceFiltersEncodedQuery = this.$filterQueryHelperService.formatQueryFromPriceFilters(this.activePriceFilters)?.priceFilters
      const currentRoutePriceFiltersEncodedQuery = this.$route.query.priceFilters
      const routePriceFiltersAreEqualToActivePriceFilters = currentActivePriceFiltersEncodedQuery === currentRoutePriceFiltersEncodedQuery

      // Check if sort option is same in store and query
      const currentSortOptionEncodedQuery = this.$filterQueryHelperService.formatQueryFromSortOption(this.activeSortOption)?.sort
      const currentRouteSortEncodedQuery = this.$route.query.sort
      const routeSortOptionIsEqualToActiveSortOption = currentSortOptionEncodedQuery === currentRouteSortEncodedQuery

      // Check if listings are cached
      const listingsAreCached =
        this.previousCategoryRoutePath === this.$route.path
        && Boolean(this.listings?.length)
        && routeFiltersAreEqualToActiveFilters
        && routePriceFiltersAreEqualToActivePriceFilters
        && routeSortOptionIsEqualToActiveSortOption

      if (listingsAreCached) {
        return
      }

      this.loading = true

      await this.setPreviousCategoryRoutePath(this.$route.path)
      await this.setActiveListingsCategory(this.currentCategory || null)
      await this.resetActiveFilters()
      await this.resetActivePriceFilters()
      await this.getInitialListings()

      // Set active filters
      if (currentRouteFiltersEncodedQuery) {
        const activeFiltersInQuery = this.$filterQueryHelperService.formatFiltersFromQueryString(currentRouteFiltersEncodedQuery)

        await this.setActiveFilters(activeFiltersInQuery)
      }

      // Set active price filters
      if (currentRoutePriceFiltersEncodedQuery) {
        const activePriceFiltersInQuery = this.$filterQueryHelperService.formatPriceFiltersFromQueryString(currentRoutePriceFiltersEncodedQuery)

        await this.setActivePriceFilters(activePriceFiltersInQuery)
      }

      // Set active sort option
      if (currentRouteSortEncodedQuery) {
        const activeSortOptionInQuery = this.$filterQueryHelperService.formatSortOptionFromQueryString(currentRouteSortEncodedQuery)

        await this.setActiveSortOption({
          sortOption: activeSortOptionInQuery,
          setQueryString: false,
        })
      }

      // Get listings with active query filters
      if (
        currentRouteFiltersEncodedQuery
        || currentRoutePriceFiltersEncodedQuery
        || currentRouteSortEncodedQuery
      ) {
        await this.fetchListingsByActiveFilters()
      }

      this.$bloomreach.viewCategory(this.currentCategory.name)

      this.loading = false
    },
    beforeDestroy() {
      window.removeEventListener('popstate', this.handlePopstateDebounced)
    },
    methods: {
      ...mapActions({
        setPreviousCategoryRoutePath: 'routes/setPreviousCategoryRoutePath',
        setListings: 'listings/setListings',
        setFilters: 'listings/setFilters',
        resetActiveFilters: 'listings/resetActiveFilters',
        resetActivePriceFilters: 'listings/resetActivePriceFilters',
        setPriceFilters: 'listings/setPriceFilters',
        setListingsTotals: 'listings/setTotals',
        setActiveListingsCategory: 'listings/setActiveCategory',
        addActiveFilterOption: 'listings/addActiveFilterOption',
        setActiveFilters: 'listings/setActiveFilters',
        fetchListingsByActiveFilters: 'listings/fetchListingsByActiveFilters',
        setActivePriceFilters: 'listings/setActivePriceFilters',
        setActiveSortOption: 'listings/setActiveSortOption',
      }),
      getInitialListings() {
        return this.getListings()
          .then(({ listings, filters, priceFilters, totals }) => {
            this.setListings(listings)
            this.setFilters(filters)
            this.setPriceFilters(priceFilters)
            this.setListingsTotals(totals)
          })
      },
      loadMoreListings() {
        this.getListings({ page: this.activeListingsTotals.page + 1 })
          .then(({ listings, totals }) => {
            this.setListings(this.listings.concat(listings))
            this.setListingsTotals(totals)
          })
      },
      getListings({ page } = {}) {
        return this.$elasticApiService
          .getListings({
            activeCategory: this.currentCategory,
            activeFilters: this.activeFilters,
            activePriceFilters: this.activePriceFilters,
            activeSortOption: this.activeSortOption,
            page,
          })
          .catch(this.handleListingsErrors)
      },
      handleListingsErrors(errors) {
        this.setListings(null)
        this.setListingsTotals(null)

        return Promise.reject(errors)
      },
      async handlePopstate() {
        const activeFiltersInQuery = this.$filterQueryHelperService.formatFiltersFromQueryString(this.$route.query.filters)
        const activePriceFiltersInQuery = this.$filterQueryHelperService.formatPriceFiltersFromQueryString(this.$route.query.priceFilters)
        const activeSortOption = this.$filterQueryHelperService.formatSortOptionFromQueryString(this.$route.query.sort)

        await this.setActiveFilters(activeFiltersInQuery)
        await this.setActivePriceFilters(activePriceFiltersInQuery)
        await this.setActiveSortOption({
          sortOption: activeSortOption,
          setQueryString: false,
          fetchListings: false,
        })

        await this.fetchListingsByActiveFilters()
      },
    },
  }
